import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';

const onboardingSteps = [
  {
    title: "Welcome to Quis Lab",
    description: "Your AI-powered professional networking platform. Let's get you started with a quick tour.",
    image: "👋"
  },
  {
    title: "Tell Us About Yourself",
    description: "Add your professional background, interests, and goals. This helps us find the most relevant connections for you. Add links to your LinkedIn, Twitter, and other social media profiles.",
    image: "👤"
  },
  {
    title: "Upload Your Resume",
    description: "Start by uploading your resume. Our AI will analyze your experience and skills to find the best connections.",
    image: "📄"
  },
  {
    title: "Refine Your Search",
    description: "Specify your search criteria to find professionals who match your interests and goals.",
    image: "🎯"
  },
  {
    title: "Connect & Engage",
    description: "Review your matches, understand why they're relevant, and start meaningful conversations.",
    image: "🤝"
  },
  {
    title: "Personalize Your Writing Style",
    description: "Add writing samples to help our AI generate emails that match your unique communication style.",
    image: "✍️"
  },
  {
    title: "Earn Rewards",
    description: "Visit the Referral Dashboard to invite friends and earn special rewards. Complete tasks to unlock exclusive benefits.",
    image: "🎁"
  }
];

const OnboardingModal = ({ isOpen, onClose }) => {
  const [currentStep, setCurrentStep] = useState(0);

  const nextStep = () => {
    if (currentStep < onboardingSteps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      onClose();
    }
  };

  const prevStep = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <motion.div
            className="relative bg-white rounded-2xl shadow-2xl max-w-2xl w-full mx-4 overflow-hidden"
            initial={{ scale: 0.85, opacity: 0, y: 40 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.85, opacity: 0, y: 40 }}
            transition={{ type: "spring", stiffness: 300, damping: 30 }}
          >
            {/* Close button */}
            <button
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-700 transition-colors"
              onClick={onClose}
              aria-label="Close"
            >
              <X className="h-6 w-6" />
            </button>

            {/* Content */}
            <div className="p-8">
              {/* Progress dots */}
              <div className="flex justify-center space-x-2 mb-8">
                {onboardingSteps.map((_, index) => (
                  <div
                    key={index}
                    className={`h-2 w-2 rounded-full transition-all duration-300 ${
                      index === currentStep ? 'bg-black w-4' : 'bg-gray-300'
                    }`}
                  />
                ))}
              </div>

              {/* Step content */}
              <div className="text-center">
                <div className="text-6xl mb-6">{onboardingSteps[currentStep].image}</div>
                <h2 className="text-2xl font-bold text-gray-900 mb-4">
                  {onboardingSteps[currentStep].title}
                </h2>
                <p className="text-gray-600 mb-8">
                  {onboardingSteps[currentStep].description}
                </p>
              </div>

              {/* Navigation buttons */}
              <div className="flex justify-between items-center">
                <button
                  onClick={prevStep}
                  className={`flex items-center text-gray-600 hover:text-gray-900 transition-colors ${
                    currentStep === 0 ? 'invisible' : ''
                  }`}
                >
                  <ChevronLeft className="h-5 w-5 mr-1" />
                  Previous
                </button>

                <button
                  onClick={nextStep}
                  className="bg-black text-white px-6 py-2 rounded-full text-sm font-medium hover:bg-gray-800 transition-colors"
                >
                  {currentStep === onboardingSteps.length - 1 ? 'Get Started' : 'Next'}
                </button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default OnboardingModal; 